/**
 * Maps REST responses onto the app's domain types.
 *
 * The REST endpoints return *named* fields, unlike the old WebMethods which
 * returned a positional `List<ListItem>` addressed as `res[35].Text`. So there
 * are no magic indices here — just names, looked up case-insensitively with a
 * short alias list, because the underlying stored procedures are the source of
 * the column names and a rename should degrade to a blank field, never a
 * mis-mapped one.
 */
import type { CustomFieldValue, Enquiry, Lead, PipelineStage } from 'types/entity'

/** `GetLeadDetailByLeadIdNew` bundles four sections in one response. */
export interface LeadDetailResponse {
  PredefinedFields?: Record<string, string> | null
  CustomFields?: Array<{ fieldid: number | string; fieldname: string; fieldvalue: string }> | null
  dealStageList?: LeadDealStage[] | null
  maskdata?: Array<{
    PageFieldId: number
    TableName: string
    UserId: number
    FieldName: string
    HiddenType: string
    IsExportAllow: boolean
  }> | null
}

export interface LeadDealStage {
  LeadPipeLineStatusId: string
  LeadId: string
  PipelineID: string
  PipeLineName: string
  PipeLineStageID: string
  PipeLineStageName: string
  Amount: number
  Probability: number
  FullName: string
  ProfileImgFileName: string
  CreatedDate: string
  CreatedBy: string
  CreatedByName: string
  Remarks: string
  EstimatedClosureDate: string
  Owner: string
}

/** Case-insensitive lookup across a list of candidate column names. */
function pick(source: Record<string, unknown> | null | undefined, ...names: string[]): string {
  if (!source) return ''

  const lowered = new Map<string, unknown>()
  for (const [key, value] of Object.entries(source)) {
    lowered.set(key.toLowerCase(), value)
  }

  for (const name of names) {
    const value = lowered.get(name.toLowerCase())
    if (value != null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

/**
 * The API returns "0" for an unset pincode / amount / id. Showing a bare 0
 * reads as real data, so those are normalised to empty.
 */
function blankIfZero(value: string): string {
  return value === '0' || value === '0.00' ? '' : value
}

function splitTags(raw: string): string[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
}

/** Joins a dial code with a number, as the profile display expects. */
function withCountryCode(code: string, number: string): string {
  if (!number) return ''
  if (!code) return number
  // Some rows already store the code inline; do not double it up.
  return number.startsWith(code) || number.startsWith('+') ? number : `${code} ${number}`
}

/**
 * Field names below were taken from a live `GetLeadDetailByLeadIdNew` response,
 * not guessed. Note the API's own inconsistent casing (`officeaddress`,
 * `modifiedon`, `followupdate`, `remark`) — `pick` is case-insensitive, and the
 * extra aliases cover older/newer builds of the same endpoint.
 */
export function mapLead(response: LeadDetailResponse | unknown, leadId: string): Lead {
  const raw = (response ?? {}) as Record<string, unknown>
  const f = ((raw.PredefinedFields as Record<string, unknown>) ?? raw) as Record<string, unknown>

  return {
    leadId,
    leadNo: pick(f, 'LeadNo', 'leadNo', 'id', 'Id'),
    personName: pick(f, 'PersonName', 'personName', 'Name', 'name', 'ContactPerson'),
    companyName: pick(f, 'CompanyName', 'companyName', 'Company', 'company'),
    mobileNo: withCountryCode(pick(f, 'CountryCode', 'countrycode'), pick(f, 'MobileNo', 'mobileNo', 'Mobile', 'mobile')),
    mobileNo1: withCountryCode(pick(f, 'CountryCode1'), pick(f, 'MobileNo1', 'mobileNo1')),
    mobileNo2: withCountryCode(pick(f, 'CountryCode2'), pick(f, 'MobileNo2', 'mobileNo2')),
    emailId: pick(f, 'EmailID', 'EmailId', 'emailId', 'Email', 'email'),
    emailId1: pick(f, 'EmailID1', 'EmailId1', 'emailId1'),
    emailId2: pick(f, 'EmailID2', 'EmailId2', 'emailId2'),
    city: pick(f, 'City', 'city', 'CityName'),
    state: pick(f, 'State', 'state', 'StateName'),
    country: pick(f, 'Country', 'country', 'CountryName'),
    pinCode: blankIfZero(pick(f, 'PinCode', 'pinCode', 'Pincode', 'pincode', 'ZipCode')),
    residentialAddress: pick(f, 'ResidentialAddress', 'residentialAddress', 'ResiAddress', 'Address'),
    // Live payload spells this "officeaddress".
    officialAddress: pick(f, 'officeaddress', 'OfficeAddress', 'OfficialAddress', 'officalAddress'),
    source: pick(f, 'SourceName', 'Source', 'source'),
    medium: pick(f, 'MediumName', 'Medium', 'medium'),
    campaign: pick(f, 'CampaignName', 'NampaignName', 'Campaign', 'campaign'),
    initialRemarks: pick(f, 'InitialRemarks', 'initialRemarks'),
    createdOn: pick(f, 'CreatedOn', 'createdOn', 'CreatedDate'),
    createdBy: pick(f, 'CreatedBy', 'CreatedByName', 'createdBy'),
    // "modifiedon" is the live spelling.
    lastUpdate: pick(f, 'modifiedon', 'ModifiedOn', 'LastUpdate', 'CreatedOn'),
    imageSuffix: pick(f, 'img_ext', 'Img_Ext'),
    /** Already a fully-qualified URL in the live payload. */
    profileImageUrl: pick(f, 'ProfileImgFileName', 'PersonImage'),
    tags: splitTags(pick(f, 'TagNames', 'LeadTags', 'Tags', 'tags')),
    stage: pick(f, 'FollowupStatus', 'StageName', 'Stage', 'stage'),
    // "followupdate" is the next-action date; "remark" is the stage note.
    reminderDate: pick(f, 'followupdate', 'NextStatusDate', 'ReminderDate'),
    stageRemark: pick(f, 'remark', 'StageRemark', 'FollowupRemarks'),
    owner: pick(f, 'AssignedTo', 'AssignedUser', 'Owner'),
    products: pick(f, 'Products'),
    amountPaid: blankIfZero(pick(f, 'AmountPaid')),
    // Ids let the edit form select the right option without matching on name.
    countryId: blankIfZero(pick(f, 'CountryId')),
    stateId: blankIfZero(pick(f, 'StateId')),
    cityId: blankIfZero(pick(f, 'CityId')),
  }
}

export function mapEnquiry(response: unknown, enquiryId: string): Enquiry {
  // GetEnquiryDetailByIdNew returns either the row object or a single-row array.
  const raw = Array.isArray(response) ? response[0] : response
  const f = (raw ?? {}) as Record<string, unknown>
  // Some builds nest the row the same way the lead endpoint does.
  const fields = (f.PredefinedFields as Record<string, unknown>) ?? f

  return {
    enquiryId: pick(fields, 'EnquiryId', 'EnquiryID', 'EnquiryNo') || enquiryId,
    personName: pick(fields, 'PersonName'),
    companyName: pick(fields, 'CompanyName'),
    mobileNo: withCountryCode(pick(fields, 'CountryCode'), pick(fields, 'MobileNo')),
    mobileNo1: withCountryCode(pick(fields, 'CountryCode1'), pick(fields, 'MobileNo1')),
    mobileNo2: withCountryCode(pick(fields, 'CountryCode2'), pick(fields, 'MobileNo2')),
    emailId: pick(fields, 'EmailId', 'EmailID'),
    emailId1: pick(fields, 'EmailId1', 'EmailID1'),
    emailId2: pick(fields, 'EmailId2', 'EmailID2'),
    city: pick(fields, 'City'),
    state: pick(fields, 'State'),
    country: pick(fields, 'Country'),
    pinCode: blankIfZero(pick(fields, 'PinCode', 'Pincode')),
    residentialAddress: pick(fields, 'ResidentialAddress'),
    officialAddress: pick(fields, 'OfficeAddress', 'OfficialAddress'),
    source: pick(fields, 'SourceName', 'Source'),
    medium: pick(fields, 'MediumName', 'Medium'),
    // "NampaignName" is a typo in the live API; the correct spelling is kept as a fallback.
    campaign: pick(fields, 'NampaignName', 'CampaignName', 'Campaign'),
    remarks: pick(fields, 'InitialRemarks', 'Remarks'),
    createdOn: pick(fields, 'CreatedOn', 'CreatedDate'),
    createdBy: pick(fields, 'CreatedBy', 'CreatedByName'),
    profileImageUrl: pick(fields, 'ProfileImgFileName'),
    tags: splitTags(pick(fields, 'TagNames', 'EnquiryTags', 'Tags')),
    stage: pick(fields, 'FollowupStatus', 'StageName', 'Stage'),
    reminderDate: pick(fields, 'followupdate', 'NextStatusDate', 'ReminderDate'),
    stageRemark: pick(fields, 'remark', 'StageRemark', 'FollowupRemarks'),
    owner: pick(fields, 'AssignedTo', 'AssignedUser', 'Owner'),
    countryId: blankIfZero(pick(fields, 'CountryId')),
    stateId: blankIfZero(pick(fields, 'StateId')),
    cityId: blankIfZero(pick(fields, 'CityId')),
  }
}

/** The lead response already carries its custom fields; no second call needed. */
export function mapCustomFieldValues(response: LeadDetailResponse | null): CustomFieldValue[] {
  return (response?.CustomFields ?? []).map((field) => ({
    label: field.fieldname ?? '',
    value: field.fieldvalue ?? '',
  }))
}

/**
 * Adapts `dealStageList` to the pipeline component's shape.
 *
 * A stage counts as reached when it carries a `LeadPipeLineStatusId`, which is
 * what the deal-stage table records for stages the lead has actually passed.
 */
export function mapPipelineStages(stages: LeadDealStage[] | null | undefined): PipelineStage[] {
  return (stages ?? []).map((stage) => ({
    PinelineName: stage.PipeLineName ?? '',
    StageName: stage.PipeLineStageName ?? '',
    PipeLineStage: stage.LeadPipeLineStatusId ? String(stage.LeadPipeLineStatusId) : '',
    Probability: stage.Probability ?? 0,
    CreatedDate: stage.CreatedDate ?? '',
    User_Login: stage.CreatedByName || stage.FullName || '',
  }))
}
